/**
 * Tracks recent API correlation IDs and JS errors for bug report diagnostics.
 *
 * This module maintains rolling buffers of recent API calls and errors so that
 * when a user submits a bug report, we can attach context to help developers
 * trace the issue.
 */

const MAX_RECENT_CALLS = 20
const MAX_RECENT_ERRORS = 10

export interface RecentApiCall {
  correlationId: string
  url: string
  method: string
  status: number | null
  timestamp: number
}

export interface RecentError {
  message: string
  stack?: string
  timestamp: number
}

const recentApiCalls: RecentApiCall[] = []
const recentErrors: RecentError[] = []

/**
 * Record an API call with its correlation ID.
 * Called from fetchOrError after each request completes.
 */
export function recordApiCall(entry: RecentApiCall): void {
  recentApiCalls.push(entry)
  if (recentApiCalls.length > MAX_RECENT_CALLS) {
    recentApiCalls.shift()
  }
}

/**
 * Get the most recent API calls (newest first).
 */
export function getRecentApiCalls(): RecentApiCall[] {
  return [...recentApiCalls].reverse()
}

/**
 * Get the most recent errors (newest first).
 */
export function getRecentErrors(): RecentError[] {
  return [...recentErrors].reverse()
}

/**
 * Format recent correlation IDs as a string for the bug report.
 * Shows correlation ID, method, URL path, status, and relative time.
 */
export function formatRecentCorrelationIds(): string {
  const calls = getRecentApiCalls()
  if (calls.length === 0) return ''

  const now = Date.now()
  return calls
    .map((call) => {
      const ago = Math.round((now - call.timestamp) / 1000)
      const urlPath = extractPath(call.url)
      const status = call.status ?? 'ERR'
      return `${call.correlationId} | ${call.method} ${urlPath} | ${status} | ${ago}s ago`
    })
    .join('\n')
}

/**
 * Format recent errors as a string for the bug report.
 */
export function formatRecentErrors(): string {
  const errors = getRecentErrors()
  if (errors.length === 0) return ''

  const now = Date.now()
  return errors
    .map((err) => {
      const ago = Math.round((now - err.timestamp) / 1000)
      const stack = err.stack ? `\n${err.stack}` : ''
      return `[${ago}s ago] ${err.message}${stack}`
    })
    .join('\n---\n')
}

function extractPath(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

export function recordError(message: string, stack?: string): void {
  recentErrors.push({ message, stack, timestamp: Date.now() })
  if (recentErrors.length > MAX_RECENT_ERRORS) {
    recentErrors.shift()
  }
}

let errorCaptureInitialized = false

/**
 * Initialize global error listeners for capturing unhandled errors.
 * Call once at app startup. Guarded against duplicate registration.
 */
export function initErrorCapture(): void {
  if (errorCaptureInitialized) return
  errorCaptureInitialized = true

  window.addEventListener('error', (event) => {
    recordError(
      event.message || 'Unknown error',
      event.error?.stack,
    )
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    if (reason instanceof Error) {
      recordError(reason.message, reason.stack)
    } else {
      recordError(String(reason))
    }
  })
}
